import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, XCircle, Loader2, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AceitarConvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  useAuth(); // ensures auth context is initialized

  const [status, setStatus] = useState("loading"); // loading | success | error | auth
  const [msg, setMsg] = useState("");
  const [familiaName, setFamiliaName] = useState("");

  useEffect(() => {
    const storedToken = localStorage.getItem("monyx_token");
    if (!storedToken) {
      setStatus("auth");
      return;
    }
    if (!token) {
      setStatus("error");
      setMsg("Token inválido.");
      return;
    }

    const accept = async () => {
      try {
        // axios interceptor already attaches Authorization header
        const res = await axios.get(`${API}/familia/aceitar-convite/${token}`);
        setFamiliaName(res.data?.familia_nome || "");
        setStatus("success");
        setTimeout(() => navigate("/app/familia"), 3000);
      } catch (err) {
        // Token expirado ou ausente → pedir login antes de aceitar
        if (err?.response?.status === 401) {
          setStatus("auth");
          return;
        }
        const detail = err?.response?.data?.detail || "Convite inválido ou expirado.";
        setMsg(detail);
        setStatus("error");
      }
    };

    accept();
  }, [token]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="fixed inset-0 grid-pattern opacity-30 pointer-events-none" />
      <span className="glow-green fixed -top-40 -left-40 pointer-events-none" />
      <span className="glow-blue fixed -bottom-40 -right-40 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md text-center">
        {/* Logo */}
        <div className="mb-8 flex items-center justify-center gap-2">
          <img src="/monyx-bot-hero.png" alt="Monyxx" className="h-10 w-10 object-contain" />
          <div className="leading-none">
            <div className="font-display text-2xl font-extrabold">
              <span className="text-emerald-400">M</span>
              <span className="text-white">ony</span>
              <span className="text-blue-400">xx</span>
            </div>
            <div className="text-[8px] tracking-[0.3em] font-bold text-emerald-400/70">FINANCE</div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur">

          {status === "loading" && (
            <>
              <div className="h-16 w-16 rounded-full bg-blue-500/15 grid place-items-center mx-auto mb-5">
                <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Verificando convite…</h2>
              <p className="text-slate-400 text-sm">Aguarde um momento.</p>
            </>
          )}

          {status === "auth" && (
            <>
              <div className="h-16 w-16 rounded-full bg-violet-500/15 grid place-items-center mx-auto mb-5">
                <Users className="h-8 w-8 text-violet-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Faça login para aceitar</h2>
              <p className="text-slate-400 text-sm mb-6">
                Você precisa estar logado para aceitar o convite familiar.
              </p>
              <Link
                to={`/login?redirect=/aceitar-convite/${token}`}
                className="inline-block w-full py-3 rounded-xl bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-400 transition">
                Entrar na conta
              </Link>
              <p className="mt-4 text-xs text-slate-500">
                Não tem conta?{" "}
                <Link to={`/register?redirect=/aceitar-convite/${token}`} className="text-emerald-400 hover:text-emerald-300">
                  Criar uma agora
                </Link>
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="h-16 w-16 rounded-full bg-emerald-500/15 grid place-items-center mx-auto mb-5">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Convite aceito!</h2>
              <p className="text-slate-400 text-sm mb-1">
                Você agora faz parte de{" "}
                {familiaName && <strong className="text-white">{familiaName}</strong>}.
              </p>
              <p className="text-slate-500 text-xs mb-6">Redirecionando para o painel familiar…</p>
              <button
                onClick={() => navigate("/app/familia")}
                className="w-full py-3 rounded-xl bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-400 transition">
                Ver Painel Familiar
              </button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="h-16 w-16 rounded-full bg-rose-500/15 grid place-items-center mx-auto mb-5">
                <XCircle className="h-8 w-8 text-rose-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Convite inválido</h2>
              <p className="text-slate-400 text-sm mb-6">{msg}</p>
              <Link
                to="/app"
                className="inline-block w-full py-3 rounded-xl bg-slate-800 border border-slate-700 text-white font-semibold text-sm hover:bg-slate-700 transition">
                Ir para o Dashboard
              </Link>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
